/**
 * CR-5858: Supplier "Read & Understood" blocking modal.
 *
 * Mounted globally (see App.tsx). When the active user is a supplier and
 * there are documents the supplier company has not yet confirmed for the
 * current version, this overlay blocks the rest of the portal until the
 * user works through the queue. Confirmation is per-company: one supplier
 * user clicking through satisfies the requirement for the whole company.
 */
import { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Loader2, FileText, ShieldCheck, ExternalLink, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { documentsAPI, type PendingCompanyConfirmation } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export default function SupplierConfirmationModal() {
  const { user, isAuthenticated } = useAuth();
  const [pending, setPending] = useState<PendingCompanyConfirmation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isSupplier = isAuthenticated
    && (user?.company_type === 'supplier'
        || (user?.platform || '').toLowerCase() === 'supplier');

  const refresh = useCallback(async () => {
    if (!isSupplier) {
      setPending(null);
      return;
    }
    try {
      setLoading(true);
      const res = await documentsAPI.getPendingCompanyConfirmations();
      setPending(res.data || []);
      setCurrentIdx(0);
      setAcknowledged(false);
    } catch (err) {
      console.error('Failed to fetch pending company confirmations', err);
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, [isSupplier]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const current = pending && pending.length > 0 ? pending[currentIdx] : null;

  const handleConfirm = useCallback(async () => {
    if (!current) return;
    setSubmitting(true);
    try {
      await documentsAPI.confirmCompany(current.id);
      toast.success('Confirmation recorded for your company');
      const next = (pending || []).filter((p) => p.id !== current.id);
      if (next.length === 0) {
        setPending([]);
      } else {
        setPending(next);
        setCurrentIdx(0);
        setAcknowledged(false);
      }
    } catch (err) {
      console.error('Failed to confirm document', err);
      toast.error('Could not record your confirmation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [current, pending]);

  const handleDownload = useCallback(async () => {
    if (!current) return;
    try {
      const res = await documentsAPI.downloadDocument(current.id);
      if (res.data.file_url) {
        window.open(res.data.file_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to open document', err);
      toast.error('Could not open the document.');
    }
  }, [current]);

  if (!isSupplier) return null;
  if (loading && pending === null) return null;
  if (!pending || pending.length === 0) return null;
  if (!current) return null;

  const total = pending.length;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      data-testid="supplier-confirmation-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-amber-100 p-2">
              <ShieldCheck className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">
                Read &amp; Understood Confirmation Required
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Your supplier company must confirm the following document
                {total > 1 ? `s (${total} outstanding)` : ''} before you can continue.
              </p>
            </div>
          </div>
        </div>

        {/* Body — current doc */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-gray-100 p-2 mt-0.5">
              <FileText className="h-5 w-5 text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium text-gray-900 truncate" title={current.title}>
                  {current.title}
                </h3>
                {current.version && (
                  <Badge variant="outline" className="text-xs">v{current.version}</Badge>
                )}
                {current.is_reconfirmation && (
                  <Badge className="text-xs bg-orange-100 text-orange-800 hover:bg-orange-100">
                    New version — reconfirm
                  </Badge>
                )}
              </div>
              {(current.uploaded_by_name || current.uploaded_by_company) && (
                <p className="text-xs text-gray-500 mt-1">
                  Issued by{' '}
                  {current.uploaded_by_company || current.uploaded_by_name || 'Customer'}
                </p>
              )}
              {current.description && (
                <p
                  className="text-sm text-gray-700 mt-2 whitespace-pre-line"
                  /* Description is plaintext on the backend, but we sanitize defensively. */
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(current.description),
                  }}
                />
              )}
            </div>
          </div>

          <div className="rounded-md border bg-gray-50 p-4 space-y-3">
            {current.acknowledgement_message ? (
              // The issuing customer's own wording (Admin Setup), as plain text.
              <p className="text-sm text-gray-700 whitespace-pre-line" data-testid="acknowledgement-message">
                {current.acknowledgement_message}
              </p>
            ) : (
              <p className="text-sm text-gray-700">
                Please open the document and review its contents before
                confirming. By confirming, you are recording on behalf of your
                company that the document has been read and understood. Once
                one authorised representative confirms, the requirement is
                cleared for your whole company until a new version is issued.
              </p>
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownload}
              >
                {current.document_type === 'url' ? (
                  <ExternalLink className="h-4 w-4 mr-2" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Open document
              </Button>
            </div>
          </div>

          {/* Acknowledgement checkbox */}
          <div className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox
              id="ack-company-confirmation"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="ack-company-confirmation"
              className="text-sm text-gray-700 cursor-pointer select-none"
            >
              I confirm on behalf of my company that this document has been
              read and understood.
            </label>
          </div>

          {total > 1 && (
            <p className="text-xs text-gray-500">
              Document {currentIdx + 1} of {total}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex-shrink-0 flex items-center justify-end gap-2">
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!acknowledged || submitting}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Confirm &amp; continue
          </Button>
        </div>
      </div>
    </div>
  );
}
